import { Marker } from "@vis.gl/react-maplibre";
import { FC } from "react";
import useEvent from "react-use-event-hook";

import {
  MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT,
  MapLibreStationDot,
  MapLibreStationPinSvg,
} from "@ryde/controls";
import {
  DirectionEnum,
  getIconMarkerTypeByRoute,
  ILocationInfo,
  MarkerIconType,
  RideStatusEnum,
  StationMarkerVariantEnum,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { IRideMapActiveStation } from "../../ride-map-station-popup";

interface IMapLibreStationMarkerProps {
  rideId: number;
  rideStatus: RideStatusEnum;
  locationInfo: Required<ILocationInfo>;
  waypointIndex: number;
  /** 1-based passenger station label; waypoints omit this and show W instead. */
  stationMarkerNumber?: number;
  direction?: DirectionEnum;
  markerBackgroundColor: string;
  isLastWayPoint: boolean;
  stationMarkerVariant: StationMarkerVariantEnum;
  isDrawing?: boolean;
  onOpen: (station: IRideMapActiveStation) => void;
  onMarkerClick?: (lat: number, lng: number) => void;
}

/**
 * One station on the planned route: a numbered pin, or a plain dot for intermediate stops in the reduced
 * live-map mode. While the course polyline is being drawn a click adds a vertex at the station instead of
 * opening its popup, so a drawn route can snap to real stops.
 */
export const MapLibreStationMarker: FC<IMapLibreStationMarkerProps> = ({
  rideId,
  rideStatus,
  locationInfo,
  waypointIndex,
  stationMarkerNumber,
  direction,
  markerBackgroundColor,
  isLastWayPoint,
  stationMarkerVariant,
  isDrawing = false,
  onOpen,
  onMarkerClick,
}) => {
  const {
    latitude,
    longitude,
    isDropoff,
    addressName,
    fullAddress,
    fullPassengersDetails,
    stationId,
    waypointCategory,
  } = locationInfo;

  const isRoutingWaypoint =
    Boolean(waypointCategory) ||
    stationMarkerVariant === StationMarkerVariantEnum.waypoint;

  const shouldUseDotMarker =
    stationMarkerVariant === StationMarkerVariantEnum.dot &&
    !isLastWayPoint &&
    !isDrawing &&
    !isRoutingWaypoint;

  const iconType = getIconMarkerTypeByRoute({
    isDropoff,
    isLastWayPoint,
    direction,
  });

  const handleClick = useEvent((e: { originalEvent: MouseEvent }) => {
    e.originalEvent.stopPropagation();
    if (isDrawing && onMarkerClick) {
      onMarkerClick(latitude, longitude);
      return;
    }
    onOpen({
      rideId,
      rideStatus,
      stationId,
      addressName: addressName ?? "",
      fullAddress: fullAddress ?? "",
      fullPassengersDetails,
      latitude,
      longitude,
      waypointIndex,
    });
  });

  // After hooks: a NaN lng/lat Marker is the ride-card crash (`reading '0'` / `'S'`). The check cannot
  // sit above `useEvent` or the hook count would change when a poll sends a bad coordinate.
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  let markerContent = (
    <MapLibreStationPinSvg
      type={iconType}
      color={markerBackgroundColor}
      label={String(stationMarkerNumber ?? "")}
    />
  );

  if (shouldUseDotMarker) {
    markerContent = (
      <MapLibreStationDot $backgroundColor={markerBackgroundColor} />
    );
  } else if (isRoutingWaypoint) {
    markerContent = (
      <MapLibreStationPinSvg
        type={MarkerIconType.Point}
        color={markerBackgroundColor}
        label="W"
        stroke={rydeColors.white}
      />
    );
  }

  return (
    <Marker
      longitude={longitude}
      latitude={latitude}
      anchor="center"
      {...MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT}
      onClick={handleClick}
    >
      <div className="maplibre-station-marker">{markerContent}</div>
    </Marker>
  );
};
